/**
 * Unit tests — the transaction designer's "Run unit test", for one screen or
 * every transaction, with each result kept, read the way a browser run is.
 *
 * The test runs on the server through the application's own engine (no
 * browser). Each screen has CHECKS: the record (add a made-up record, list
 * it, open it, edit it, delete it) and one per action button (a new record
 * the button's rules accept, the button pressed, the record removed). The
 * list shows every check grouped by screen and filtered to the failures; a
 * failure is fixed with AI on its own, or all of them in one go, and its
 * screen re-tested. It says whether a screen's model WORKS; the browser test
 * cases say whether a person can USE it.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ExternalLink, Loader2, Play, RotateCcw, Sparkles, Square } from 'lucide-react'
import Button from '@/components/common/Button'
import Spinner from '@/components/common/Spinner'
import {
  apiError, cancelUnitTests, listUnitTests, runUnitTests, type UnitTestJob, type UnitTestRow,
} from '@/api/uiTesting'
import type { UnitTestCheck, UnitTestReport, UnitTestStep } from '@/api/unitTestAgent'
import {
  Badge, NumberDot, ShareBar, TONE, ago, fmtTime, mutedText, panel, type Tone, type UnitFixItem,
} from './shared'

interface Props {
  project: string
  canTest: boolean
  canFix: boolean
  /** Hand failed checks (one, or all) to the Ask AI panel to fix. */
  onFix: (items: UnitFixItem[]) => void
  /** A job finished — the page refreshes its tab badge. */
  onChanged: () => Promise<void> | void
}

const POLL_MS = 2000
const SPIN = { animation: 'spin 1s linear infinite' }
const RECORD_LABEL = 'Add, list, open, edit and delete a record'

type State = 'passed' | 'failed' | 'skipped' | 'never' | 'unbuilt'
type Filter = 'failed' | 'notrun' | 'passed' | 'all'

interface Item {
  key: string
  row: UnitTestRow
  check: UnitTestCheck | null
  state: State
}

/** The account that ran it, short (the whole address is the tooltip). */
const who = (by: string) => (by.includes('@') ? by.split('@')[0] : by)

/** A result kept before checks existed is one record check. */
function checksOf(rep: UnitTestReport): UnitTestCheck[] {
  if (rep.checks?.length) return rep.checks
  return [{
    id: 'record', kind: 'record', name: '', label: RECORD_LABEL, steps: rep.steps?.length ?? 0,
    status: rep.ok ? 'passed' : 'failed', first_failure: rep.first_failure ?? null,
  }]
}

const stepsOf = (rep: UnitTestReport, check: UnitTestCheck): UnitTestStep[] =>
  (rep.steps || []).filter((s) => (s.check || 'record') === check.id)

const stateTone = (s: State): Tone =>
  s === 'passed' ? 'ok' : s === 'failed' ? 'err' : s === 'skipped' ? 'warn' : 'neutral'

const STATE_WORD: Record<State, string> = {
  passed: 'Passed', failed: 'Failed', skipped: 'Not run', never: 'Never run', unbuilt: 'Not built yet',
}

function itemsOf(rows: UnitTestRow[]): Item[] {
  const out: Item[] = []
  for (const r of rows) {
    if (!r.built) { out.push({ key: `${r.obj_name}/`, row: r, check: null, state: 'unbuilt' }); continue }
    const rep = r.result?.report
    if (!rep) { out.push({ key: `${r.obj_name}/`, row: r, check: null, state: 'never' }); continue }
    for (const c of checksOf(rep)) out.push({ key: `${r.obj_name}/${c.id}`, row: r, check: c, state: c.status })
  }
  return out
}

const fixItem = (it: Item): UnitFixItem => ({
  obj: it.row.obj_name, title: it.row.title, check: it.check?.label || RECORD_LABEL,
  step: it.check?.first_failure?.step, message: it.check?.first_failure?.message,
})

export default function UnitTestsTab({ project, canTest, canFix, onFix, onChanged }: Props) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<UnitTestRow[] | null>(null)
  const [job, setJob] = useState<UnitTestJob | null>(null)
  const [filter, setFilter] = useState<Filter | null>(null)
  const [picked, setPicked] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const wasRunning = useRef(false)

  const load = useCallback(async () => {
    try {
      const r = await listUnitTests(project)
      setRows(r.objects)
      setJob(r.job)
      if (wasRunning.current && !r.job?.running && r.job) {
        const j = r.job
        if (j.status === 'error') toast.error(`Unit tests stopped: ${j.error || 'see the server log'}`)
        else toast.success(`Unit tests ${j.status === 'cancelled' ? 'stopped' : 'finished'} — `
          + `${j.passed} screen${j.passed === 1 ? '' : 's'} passed, ${j.failed} failed`)
        void onChanged()
      }
      wasRunning.current = !!r.job?.running
    } catch (err) {
      toast.error(apiError(err, 'Could not load the unit tests'))
      setRows((prev) => prev ?? [])
    }
  }, [project, onChanged])

  useEffect(() => { void load() }, [load])

  // Poll while a job is going.
  useEffect(() => {
    if (!job?.running) return
    const t = window.setInterval(() => { void load() }, POLL_MS)
    return () => window.clearInterval(t)
  }, [job?.running, load])

  const items = useMemo(() => itemsOf(rows || []), [rows])
  const count = (f: Filter) => items.filter((it) => matches(it, f)).length
  const activeFilter: Filter = filter ?? (count('failed') > 0 ? 'failed' : 'all')
  const shown = useMemo(() => items.filter((it) => matches(it, activeFilter)), [items, activeFilter])

  // Keep a check picked: the chosen one while it is shown, else the first shown.
  useEffect(() => {
    if (picked && shown.some((it) => it.key === picked)) return
    setPicked(shown[0]?.key ?? null)
  }, [shown, picked])

  const groups = useMemo(() => {
    const m = new Map<string, Item[]>()
    for (const it of shown) m.set(it.row.obj_name, [...(m.get(it.row.obj_name) || []), it])
    return Array.from(m.entries())
  }, [shown])

  const start = async (objects: string[] | undefined, key: string) => {
    setBusy(key)
    try {
      const r = await runUnitTests(project, objects)
      toast.success(`Testing ${r.total} screen${r.total === 1 ? '' : 's'}`)
      wasRunning.current = true
      await load()
    } catch (err) {
      toast.error(apiError(err, 'Could not start the unit tests'))
    } finally {
      setBusy(null)
    }
  }

  const stop = async () => {
    setBusy('stop')
    try {
      await cancelUnitTests(project)
      toast('Stopping after the screen being tested now')
      await load()
    } catch (err) {
      toast.error(apiError(err, 'Could not stop the unit tests'))
    } finally {
      setBusy(null)
    }
  }

  if (rows === null) {
    return <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner /></div>
  }

  const running = !!job?.running
  const queued = new Set(running ? (job?.objects || []).slice(job?.done || 0) : [])
  const built = rows.filter((r) => r.built)
  const failedItems = items.filter((it) => it.state === 'failed')
  const failedScreens = Array.from(new Set(failedItems.map((it) => it.row.obj_name)))
  const passedScreens = built.filter((r) => r.result?.report.ok).length
  const testedScreens = built.filter((r) => r.result).length
  const pickedItem = items.find((it) => it.key === picked) || null

  const pill = (f: Filter, label: string, tone: Tone) => {
    const on = activeFilter === f
    return (
      <button key={f} type="button" aria-pressed={on} onClick={() => { setFilter(f); setPicked(null) }} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 36, padding: '0 14px',
        borderRadius: 'var(--radius-full)', fontSize: 14, fontWeight: 600,
        border: `1px solid ${on ? TONE[tone].fg : 'var(--color-border)'}`,
        background: on ? TONE[tone].bg : 'transparent',
        color: on ? TONE[tone].fg : 'var(--color-text-secondary)',
      }}>{label} <strong>{count(f)}</strong></button>
    )
  }

  const screenState = (r: UnitTestRow): ReactNode => {
    if (running && job?.current === r.obj_name) return <Badge tone="info"><Loader2 size={13} style={SPIN} /> Testing…</Badge>
    if (queued.has(r.obj_name)) return <Badge tone="neutral">Queued</Badge>
    return null
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section style={{ ...panel, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          {testedScreens === 0
            ? 'No unit tests have run yet'
            : `${passedScreens} of ${built.length} screen${built.length === 1 ? '' : 's'} pass their unit test`
              + (failedItems.length ? ` · ${failedItems.length} failed check${failedItems.length === 1 ? '' : 's'}` : '')}
        </h2>
        <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 15, lineHeight: 1.55, maxWidth: 900 }}>
          A unit test checks that a screen&rsquo;s model works, through the application&rsquo;s own engine and
          with no browser: it adds a made-up record, finds it in the list, opens it, edits it and deletes it,
          then presses each action button on a new record that button&rsquo;s rules accept. A button that only
          opens something in the browser (a report, another screen, a wizard) is left to the browser test cases.
          A save that needs approval is sent for approval, withdrawn, and saved as if approved.
        </p>
        {running && job && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 15 }}>
              Testing — {job.done} of {job.total} screens done. Results appear as each screen finishes.
            </span>
            <ShareBar label="Unit test progress" parts={[
              { value: job.passed, tone: 'ok' }, { value: job.failed, tone: 'err' },
              { value: Math.max(0, job.total - job.done), tone: 'neutral' },
            ]} />
          </div>
        )}
      </section>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div role="group" aria-label="Show" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {pill('failed', 'Failed', 'err')}
          {pill('notrun', 'Not run', 'warn')}
          {pill('passed', 'Passed', 'ok')}
          {pill('all', 'All', 'info')}
        </div>
        {canTest && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {running ? (
              <Button variant="secondary" icon={<Square size={14} />} loading={busy === 'stop'}
                      onClick={() => void stop()}>Stop</Button>
            ) : (
              <>
                {canFix && failedItems.length > 1 && (
                  <Button variant="secondary" icon={<Sparkles size={15} />}
                          onClick={() => onFix(failedItems.map(fixItem))}>
                    Fix all {failedItems.length} failures with AI
                  </Button>
                )}
                {failedScreens.length > 0 && (
                  <Button variant="secondary" icon={<RotateCcw size={15} />} loading={busy === 'failed'}
                          onClick={() => void start(failedScreens, 'failed')}>
                    Run the {failedScreens.length} screen{failedScreens.length === 1 ? '' : 's'} that didn’t pass again
                  </Button>
                )}
                <Button icon={<Play size={15} />} disabled={built.length === 0} loading={busy === 'all'}
                        onClick={() => void start(undefined, 'all')}>Run all</Button>
              </>
            )}
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{ ...panel, padding: 24, ...mutedText, fontSize: 15 }}>
          This application has no transaction screens in its menu.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
          <section aria-label="Checks" style={{ ...panel, flex: '1 1 320px', minWidth: 0, overflow: 'hidden' }}>
            {groups.length === 0 && (
              <div style={{ ...mutedText, fontSize: 15, padding: 16 }}>No checks with this result.</div>
            )}
            {groups.map(([obj, list]) => {
              const row = list[0].row
              const all = items.filter((it) => it.row.obj_name === obj)
              const f = all.filter((it) => it.state === 'failed').length
              const p = all.filter((it) => it.state === 'passed').length
              return (
                <div key={obj}>
                  <div style={{
                    padding: '12px 16px', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center',
                    borderTop: '1px solid var(--color-border)', marginTop: -1, flexWrap: 'wrap',
                  }}>
                    <strong style={{ fontSize: 15 }}>{row.title}</strong>
                    {screenState(row) || (row.built && row.result ? (
                      <span style={{ fontSize: 14, color: f ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
                        {f ? `${f} failed · ` : ''}{p} passed
                      </span>
                    ) : null)}
                  </div>
                  {list.map((it) => {
                    const on = it.key === picked
                    return (
                      <button key={it.key} type="button" onClick={() => setPicked(it.key)} aria-current={on} style={{
                        width: '100%', textAlign: 'left', display: 'flex', gap: 12, alignItems: 'flex-start',
                        padding: '12px 16px', borderTop: '1px solid var(--color-border)',
                        background: on ? 'var(--color-accent-weak)' : 'transparent',
                        boxShadow: on ? 'inset 3px 0 0 var(--color-accent)' : 'none', color: 'var(--color-text)',
                      }}>
                        <StatusIcon state={it.state} />
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                          <span style={{ fontWeight: 600, fontSize: 15 }}>
                            {it.check?.label || (it.state === 'unbuilt' ? 'Not built yet' : 'Never run')}
                          </span>
                          <span style={{ ...mutedText, fontSize: 14, overflowWrap: 'anywhere' }}>{itemLine(it)}</span>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )
            })}
          </section>

          <section aria-label="Check result" style={{ ...panel, flex: '999 1 600px', minWidth: 0, padding: 24 }}>
            {pickedItem ? (
              <CheckDetail key={pickedItem.key} item={pickedItem} canTest={canTest} canFix={canFix}
                           running={running} busy={busy}
                           onFix={() => onFix([fixItem(pickedItem)])}
                           onRun={() => void start([pickedItem.row.obj_name], `run-${pickedItem.row.obj_name}`)}
                           onDesigner={() => navigate(`/designers/transaction/${encodeURIComponent(project)}/`
                             + encodeURIComponent(pickedItem.row.obj_name))} />
            ) : <div style={mutedText}>Pick a check to see what happened.</div>}
          </section>
        </div>
      )}
    </div>
  )
}

function matches(it: Item, f: Filter): boolean {
  if (f === 'all') return true
  if (f === 'failed') return it.state === 'failed'
  if (f === 'passed') return it.state === 'passed'
  return it.state === 'skipped' || it.state === 'never' || it.state === 'unbuilt'
}

function itemLine(it: Item): string {
  if (it.state === 'failed') {
    const ff = it.check?.first_failure
    const msg = ff?.message || ff?.code || ''
    const short = msg.length > 80 ? `${msg.slice(0, 80)}…` : msg
    return `Failed${ff?.step ? ` at “${ff.step}”` : ''}${short ? ` · ${short}` : ''}`
  }
  if (it.state === 'never') return 'This screen has not been unit-tested yet'
  if (it.state === 'unbuilt') return 'In the menu, but it has no model to test'
  return STATE_WORD[it.state]
}

function StatusIcon({ state }: { state: State }) {
  const c = TONE[stateTone(state)].fg
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" style={{ flex: 'none', marginTop: 1 }}>
      <circle cx="9" cy="9" r="8" fill={c} />
      {state === 'passed' && <path d="M5.5 9.2l2.3 2.3 4.7-4.9" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
      {state === 'failed' && <path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />}
      {state !== 'passed' && state !== 'failed' && <path d="M5.5 9h7" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />}
    </svg>
  )
}

// ── one check ───────────────────────────────────────────────────────────────

function CheckDetail({ item, canTest, canFix, running, busy, onFix, onRun, onDesigner }: {
  item: Item; canTest: boolean; canFix: boolean; running: boolean; busy: string | null
  onFix: () => void; onRun: () => void; onDesigner: () => void
}) {
  const { row, check, state } = item
  const result = row.result
  const rep = result?.report
  const steps = rep && check ? stepsOf(rep, check) : []
  const ff = check?.first_failure
  const should = check?.kind === 'action'
    ? `The “${check.name}” button runs on a record its rules accept, without an error.`
    : 'The made-up record is added, found in the list, opened, edited and deleted without an error.'
  const did = check?.kind === 'action'
    ? `The “${check.name}” button ran on a record its rules accept, without an error.`
    : 'The made-up record was added, found in the list, opened, edited and deleted without an error.'
  const why = state === 'skipped' ? (steps.find((s) => s.status === 'SKIP')?.detail || 'It was not run.') : ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
          {row.obj_name}{check ? ` · ${check.id}` : ''}
        </span>
        <h2 style={{ margin: 0, fontSize: 23, fontWeight: 600, lineHeight: 1.3 }}>
          {row.title}: {check?.label || (state === 'unbuilt' ? 'not built yet' : 'never run')}
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 14, color: 'var(--color-text-secondary)' }}>
          <Badge tone={stateTone(state)}>{STATE_WORD[state]}</Badge>
          {result && (
            <span title={result.by}>
              Ran {ago(result.at)} ({fmtTime(result.at)}){result.by ? ` · by ${who(result.by)}` : ''}
              {result.via ? ` · via ${result.via}` : ''}
            </span>
          )}
          {row.folder && <span>· {row.folder}</span>}
        </div>
      </div>

      {state === 'failed' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          <Outcome tone="ok" label="Should happen" text={should} />
          <Outcome tone="err" label="What happened"
                   text={`${ff?.step ? `“${ff.step}” failed: ` : ''}${ff?.message || ff?.code || 'A step did not do what it should.'}`} />
        </div>
      )}
      {state === 'passed' && <Outcome tone="ok" label="Passed" text={did} />}
      {state === 'skipped' && <Outcome tone="warn" label="Not run by the unit test" text={why} />}
      {state === 'never' && <Outcome tone="neutral" label="Never run" text="Run this screen's unit test to check it." />}
      {state === 'unbuilt' && <Outcome tone="neutral" label="Not built yet" text="This menu entry has no model yet, so there is nothing to test." />}

      {steps.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>What the test did</h3>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
            {steps.map((s, i) => {
              const tone: Tone = s.status === 'PASS' ? 'ok' : s.status === 'FAIL' ? 'err' : 'neutral'
              return (
                <li key={i} style={{
                  display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0',
                  borderTop: '1px solid var(--color-border)',
                }}>
                  <NumberDot n={i + 1} tone={tone} />
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 15, color: tone === 'err' ? 'var(--color-error)' : 'var(--color-text)' }}>
                      {s.name}
                      <span style={{ ...mutedText, fontSize: 14 }}>
                        {' '}· {s.status === 'PASS' ? 'passed' : s.status === 'FAIL' ? 'failed' : 'not run'}
                      </span>
                    </span>
                    {s.detail && (
                      <span style={{ fontSize: 14, color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>{s.detail}</span>
                    )}
                    {s.issue?.message && (
                      <span style={{ fontSize: 14, color: 'var(--color-error)', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                        {s.issue.code ? `${s.issue.code}: ` : ''}{s.issue.message}
                      </span>
                    )}
                    {(s.issue?.field_errors || []).length > 0 && (
                      <ul style={{ margin: '2px 0 0', paddingLeft: 18, fontSize: 14 }}>
                        {(s.issue?.field_errors || []).map((fe, j) => (
                          <li key={j}>{fe.field ? <code>{fe.field}</code> : null} {fe.message}</li>
                        ))}
                      </ul>
                    )}
                  </span>
                </li>
              )
            })}
          </ol>
        </div>
      )}

      {traceOf(steps) && (
        <details>
          <summary style={{ cursor: 'pointer', ...mutedText, fontSize: 14 }}>For specialists: the server&rsquo;s stack trace</summary>
          <pre style={{ fontSize: 13, maxHeight: 240, overflow: 'auto', margin: '8px 0 0' }}>{traceOf(steps)}</pre>
        </details>
      )}

      {(canTest || canFix) && row.built && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
          {state === 'failed' && canFix && (
            <Button icon={<Sparkles size={15} />} disabled={running} onClick={onFix}>Fix with AI and re-test</Button>
          )}
          {canTest && (
            <Button variant="secondary" icon={state === 'never' ? <Play size={15} /> : <RotateCcw size={15} />}
                    disabled={running} loading={busy === `run-${row.obj_name}`} onClick={onRun}>
              {state === 'never' ? 'Run this screen’s unit test' : 'Run this screen again'}
            </Button>
          )}
          <Button variant="ghost" icon={<ExternalLink size={15} />} onClick={onDesigner}>Open in the designer</Button>
        </div>
      )}
    </div>
  )
}

/** The first stack trace among the steps (an unexpected server error). */
function traceOf(steps: UnitTestStep[]): string {
  return steps.find((s) => s.issue?.traceback)?.issue?.traceback || ''
}

function Outcome({ tone, label, text }: { tone: Tone; label: string; text: string }) {
  return (
    <div style={{
      border: `1px solid ${TONE[tone].fg}`, background: TONE[tone].bg, borderRadius: 10, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <span style={{ color: TONE[tone].fg, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
      <span style={{ lineHeight: 1.5, fontSize: 15, overflowWrap: 'anywhere' }}>{text}</span>
    </div>
  )
}
